import React, { useState } from 'react';
import styles from './SuperAdminDataEntry.module.scss';
import {
    useGetCustomizePage,
    useCreatePrompt,
    useUpdatePrompt,
    useDeletePrompt,
    useUpdateWorkflowDefinition,
} from 'services/hooks/useSuperAdminDataEntry';
import AddPromptModal from 'components/AddPromptModal';
import AlertModal from 'components/AlertModal';
import { Select, Modal, Input, TextArea, Button, Chip } from 'common/components';
import type { AlertType, LanguageDisplay, Prompt, CreatePromptRequest, Language } from 'types/superAdminDataEntry.type';

const SuperAdminDataEntry: React.FC = () => {
    // React Query hooks
    const { customizePageData, isCustomizePageLoading } = useGetCustomizePage();
    const { createPrompt } = useCreatePrompt();
    const { updatePrompt } = useUpdatePrompt();
    const { deletePrompt } = useDeletePrompt();
    const { updateWorkflowDefinition } = useUpdateWorkflowDefinition();

    // State for Prompt Management
    const [selectedPrompt, setSelectedPrompt] = useState<string>('');
    const [promptText, setPromptText] = useState<string>('');
    const [originalPromptText, setOriginalPromptText] = useState<string>(''); // Store original text for reset
    const [isNewPrompt, setIsNewPrompt] = useState<boolean>(true);
    const [promptName, setPromptName] = useState<string>('');
    const [promptDescription, setPromptDescription] = useState<string>('');
    const [isAddPromptModalOpen, setIsAddPromptModalOpen] = useState<boolean>(false);
    const [selectedLanguage, setSelectedLanguage] = useState<LanguageDisplay>('English');

    // State for Agent Configuration
    const [selectedWorkflow, setSelectedWorkflow] = useState<string>('');
    const [configurationText, setConfigurationText] = useState<string>('');
    const [originalConfigurationText, setOriginalConfigurationText] = useState<string>(''); // Store original JSON for reset

    // State for Alert Modal
    const [isAlertOpen, setIsAlertOpen] = useState<boolean>(false);
    const [alertMessage, setAlertMessage] = useState<string>('');
    const [alertType, setAlertType] = useState<AlertType>('info');

    // State for delete confirmation
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState<boolean>(false);
    const [promptToDelete, setPromptToDelete] = useState<Prompt | null>(null);

    // Derive data from React Query
    const prompts = customizePageData?.prompts || [];
    const tenants = customizePageData?.tenants || [];
    const workflows = customizePageData?.agentConfigs || [];
    const loadingPrompts = isCustomizePageLoading;
    const loadingConfig = isCustomizePageLoading;

    // Function to show alerts
    const showAlert = (message: string, type: AlertType = 'info') => {
        setAlertMessage(message);
        setAlertType(type);
        setIsAlertOpen(true);
    };

    // Function to close alert
    const closeAlert = () => {
        setIsAlertOpen(false);
    };

    // Handle prompt select
    const handlePromptSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const promptId = e.target.value;
        setSelectedPrompt(promptId);

        if (!promptId) {
            // Reset fields if "Select prompt" option is selected
            setIsNewPrompt(true);
            setPromptName('');
            setPromptDescription('');
            setPromptText('');
            setOriginalPromptText('');
            setSelectedLanguage('English');
            return;
        }

        // Find selected prompt in the prompts array
        const selectedPromptObj = prompts.find((p) => p.id === promptId);

        if (selectedPromptObj) {
            setIsNewPrompt(false);
            // Use the same field priority as displayed in the dropdown
            setPromptName(selectedPromptObj.prompt_name || selectedPromptObj.title || selectedPromptObj.name || '');
            setPromptDescription(selectedPromptObj.description || '');

            const promptTextValue = selectedPromptObj.prompt || '';
            setPromptText(promptTextValue);
            setOriginalPromptText(promptTextValue);

            // Map language code to full name for dropdown
            const language = selectedPromptObj.language;
            if (language) {
                // Normalize language to lowercase for consistent comparison
                const normalizedLanguage = language.toLowerCase();

                if (normalizedLanguage === 'en' || normalizedLanguage === 'english') {
                    setSelectedLanguage('English');
                } else if (normalizedLanguage === 'es' || normalizedLanguage === 'spanish') {
                    setSelectedLanguage('Spanish');
                } else {
                    // For any other language, capitalize first letter
                    setSelectedLanguage(
                        (language.charAt(0).toUpperCase() + language.slice(1).toLowerCase()) as LanguageDisplay
                    );
                }
            } else {
                setSelectedLanguage('English'); // Default to English if no language specified
            }
        }
    };

    // Initial delete prompt handler - shows confirmation
    const handleDeletePrompt = () => {
        if (!selectedPrompt) {
            showAlert('Please select a prompt to delete', 'warning');
            return;
        }

        // Find the prompt to be deleted
        const promptObj = prompts.find((p) => p.id === selectedPrompt);
        if (!promptObj) {
            showAlert('Selected prompt not found', 'error');
            return;
        }

        // Create the display name with tenant if available
        const displayName = promptObj.prompt_name || promptObj.title || promptObj.name;
        const tenantName = promptObj.edges?.tenant?.name;
        const fullDisplayName = tenantName ? `${displayName} - ${tenantName}` : displayName;

        // Store the prompt to delete and show confirmation modal
        setPromptToDelete(promptObj);
        setAlertMessage(`Are you sure you want to delete the prompt "${fullDisplayName}"?`);
        setIsDeleteConfirmOpen(true);
    };

    // Handle confirmed deletion
    const confirmDeletePrompt = async () => {
        if (!promptToDelete) return;

        try {
            await deletePrompt(promptToDelete.id);

            // Reset form
            setSelectedPrompt('');
            setPromptText('');
            setOriginalPromptText('');
            setPromptName('');
            setPromptDescription('');
            setSelectedLanguage('English');
            setIsNewPrompt(true);

            // Close confirmation modal
            setIsDeleteConfirmOpen(false);
            setPromptToDelete(null);

            // Show success message
            showAlert('Prompt deleted successfully!', 'success');
        } catch (error) {
            console.error('Error deleting prompt:', error);
            setIsDeleteConfirmOpen(false);
            setPromptToDelete(null);
            showAlert(`Error deleting prompt: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
        }
    };

    // Handle cancel deletion
    const cancelDeletePrompt = () => {
        setIsDeleteConfirmOpen(false);
        setPromptToDelete(null);
    };

    // Handle add new prompt - now opens the modal
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleAddPrompt = () => {
        setIsAddPromptModalOpen(true);
    };

    // Handle save prompt from modal
    const handleSavePromptFromModal = async (promptData: {
        title: string;
        description: string;
        prompt: string;
        language: string;
        tenantId: string;
    }) => {
        try {
            // Convert language to lowercase full name (english or spanish)
            const languageCode: Language = promptData.language.toLowerCase() === 'spanish' ? 'spanish' : 'english';

            // Build the complete CreatePromptRequest with langfuseName
            const completePromptData: CreatePromptRequest = {
                title: promptData.title,
                description: promptData.description,
                prompt: promptData.prompt,
                language: languageCode,
                tenantId: promptData.tenantId,
                langfuseName: promptData.title, // Use title as langfuseName
            };

            // Call mutation to create prompt
            await createPrompt(completePromptData);

            // Close modal
            setIsAddPromptModalOpen(false);

            // Show success message
            showAlert('New prompt created!', 'success');
        } catch (error) {
            console.error('Error creating prompt:', error);
            showAlert(`Error creating prompt: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
        }
    };

    // Handle language change
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedLanguage(e.target.value as LanguageDisplay);
    };

    // Handle clear prompt text
    const handleClearPromptText = () => {
        setPromptText('');
    };

    // Handle reset prompt text to original
    const handleResetPromptText = () => {
        setPromptText(originalPromptText);
    };

    // Handle save prompt changes
    const handleSavePromptChanges = async () => {
        if (isNewPrompt && !promptName.trim()) {
            showAlert('Please enter a prompt name.', 'error');
            return;
        }

        try {
            // Convert language selection to lowercase full name (english or spanish)
            const languageCode: Language = selectedLanguage.toLowerCase() === 'spanish' ? 'spanish' : 'english';

            if (isNewPrompt) {
                // For new prompts, we need tenantId and langfuseName
                // This path shouldn't normally be used as the modal handles new prompts
                showAlert('Please use the "Add Prompt" button to create new prompts', 'warning');
                return;
            } else {
                // Update existing prompt
                const promptData = {
                    title: promptName.trim(),
                    description: promptDescription,
                    prompt: promptText,
                    language: languageCode,
                    langfuseName: promptName.trim(), // Using title as langfuseName
                };

                await updatePrompt({ promptId: selectedPrompt, promptData });
                showAlert('Prompt updated!', 'success');
            }

            // Reset form and dropdown after successful save
            setSelectedPrompt('');
            setPromptText('');
            setOriginalPromptText('');
            setPromptName('');
            setPromptDescription('');
            setSelectedLanguage('English');
            setIsNewPrompt(true);
        } catch (error) {
            console.error('Error saving prompt:', error);
            showAlert(`Error saving prompt: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
        }
    };

    // Handle workflow select
    const handleWorkflowSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const workflowId = e.target.value;
        setSelectedWorkflow(workflowId);

        if (!workflowId) {
            setConfigurationText('');
            setOriginalConfigurationText(''); // Reset original text as well
            return;
        }

        // Find selected workflow in the workflows array
        const selectedWorkflowObj = workflows.find((w) => w.id === workflowId);

        if (selectedWorkflowObj) {
            try {
                // Check if workflow_definition is a string or object
                const configText =
                    typeof selectedWorkflowObj.workflow_definition === 'string'
                        ? selectedWorkflowObj.workflow_definition
                        : JSON.stringify(selectedWorkflowObj.workflow_definition, null, 2);

                setConfigurationText(configText);
                setOriginalConfigurationText(configText); // Store original for reset
            } catch (error) {
                console.error('Error parsing workflow definition:', error);
                setConfigurationText('{}');
                setOriginalConfigurationText('{}');
            }
        }
    };

    // Handle save workflow definition
    const handleSaveWorkflowDefinition = async () => {
        if (configurationText.trim() === '') {
            showAlert('Please enter configuration', 'warning');
            return;
        }

        if (!selectedWorkflow) {
            showAlert('Please select a workflow', 'warning');
            return;
        }

        try {
            // Validate JSON
            const configObj = JSON.parse(configurationText);

            // Only send the id and workflow_definition fields
            const updatedWorkflow = {
                workflowId: selectedWorkflow,
                workflowDefinition: configObj,
            };

            // Update the workflow definition using React Query mutation
            await updateWorkflowDefinition(updatedWorkflow);

            // Reset form and dropdown after successful save
            setSelectedWorkflow('');
            setConfigurationText('');

            showAlert('Workflow definition updated!', 'success');
        } catch (error) {
            if (error instanceof SyntaxError) {
                showAlert('Invalid JSON format', 'error');
            } else {
                console.error('Error saving workflow definition:', error);
                showAlert(
                    `Error saving workflow definition: ${error instanceof Error ? error.message : 'Unknown error'}`,
                    'error'
                );
            }
        }
    };

    // Handle clear JSON editor
    const handleClearJSON = () => {
        setConfigurationText('');
    };

    // Handle reset JSON editor to original
    const handleResetJSON = () => {
        setConfigurationText(originalConfigurationText);
    };

    return (
        <div className={styles.superAdminDataEntryContainer}>
            <h1 className={styles.pageTitle}>Super Admin Data Entry</h1>

            <div className={styles.panelContainer}>
                {/* Prompt Management Panel */}
                <div className={`${styles.panel} ${styles.promptPanel}`}>
                    <h2 className={styles.panelTitle}>Prompt Management</h2>

                    <div className={styles.formGroup}>
                        <Select
                            value={selectedPrompt}
                            onChange={handlePromptSelect}
                            disabled={loadingPrompts}
                            options={[
                                { value: '', label: 'Select previous prompt' },
                                ...prompts.map((prompt) => ({
                                    value: prompt.id,
                                    label:
                                        prompt.prompt_name ||
                                        prompt.title ||
                                        prompt.name +
                                            (prompt.edges?.tenant?.name ? ` - ${prompt.edges.tenant.name}` : ''),
                                })),
                            ]}
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <Input
                            id='prompt-title'
                            type='text'
                            label='Title:'
                            value={promptName}
                            onChange={(e) => setPromptName(e.target.value)}
                            placeholder='Enter prompt title...'
                            disabled={loadingPrompts}
                        />
                    </div>

                    <div className={styles.languageSelector}>
                        <label className={styles.languageLabel}>Language:</label>
                        <div className={styles.languageChips}>
                            <div
                                onClick={() => {
                                    if (!loadingPrompts) {
                                        setSelectedLanguage('English');
                                    }
                                }}
                                style={{ cursor: loadingPrompts ? 'not-allowed' : 'pointer' }}
                            >
                                <Chip
                                    variant={selectedLanguage === 'English' ? 'filled' : 'outlined'}
                                    color='primary'
                                    size='md'
                                    corner='pill'
                                    clickable
                                    disabled={loadingPrompts}
                                >
                                    English
                                </Chip>
                            </div>
                            <div
                                onClick={() => {
                                    if (!loadingPrompts) {
                                        setSelectedLanguage('Spanish');
                                    }
                                }}
                                style={{ cursor: loadingPrompts ? 'not-allowed' : 'pointer' }}
                            >
                                <Chip
                                    variant={selectedLanguage === 'Spanish' ? 'filled' : 'outlined'}
                                    color='primary'
                                    size='md'
                                    corner='pill'
                                    clickable
                                    disabled={loadingPrompts}
                                >
                                    Spanish
                                </Chip>
                            </div>
                        </div>
                    </div>

                    <div className={styles.formGroup}>
                        <div className={styles.textareaWrapper}>
                            <TextArea
                                id='prompt-text'
                                label='Prompt Text:'
                                className={styles.promptTextarea}
                                value={promptText}
                                onChange={(e) => setPromptText(e.target.value)}
                                placeholder='Enter your AI prompt here...'
                                rows={10}
                                disabled={loadingPrompts}
                            />
                            <div className={styles.textareaButtons}>
                                <Button
                                    className={`${styles.textareaBtn} ${styles.clearBtn}`}
                                    onClick={handleClearPromptText}
                                    disabled={loadingPrompts || !promptText}
                                    title='Clear text'
                                >
                                    Clear
                                </Button>
                                <Button
                                    className={`${styles.textareaBtn} ${styles.resetBtn}`}
                                    onClick={handleResetPromptText}
                                    disabled={loadingPrompts || promptText === originalPromptText || !selectedPrompt}
                                    title='Reset to original'
                                >
                                    Reset
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className={styles.buttonContainer}>
                        <div className={styles.leftButtons}>
                            <Button variant='primary' onClick={() => setIsAddPromptModalOpen(true)}>
                                + Add Prompt
                            </Button>

                            <Button
                                variant='danger'
                                onClick={handleDeletePrompt}
                                disabled={!selectedPrompt || isNewPrompt}
                            >
                                Delete Prompt
                            </Button>
                        </div>

                        <div className={styles.rightButtons}>
                            <Button variant='primary' onClick={handleSavePromptChanges} disabled={loadingPrompts}>
                                Save Changes
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Workflow Definition Panel */}
                <div className={`${styles.panel} ${styles.configPanel}`}>
                    <h2 className={styles.panelTitle}>Workflow Definition</h2>

                    <div className={styles.formGroup}>
                        <Select
                            value={selectedWorkflow}
                            onChange={handleWorkflowSelect}
                            disabled={loadingConfig}
                            options={[
                                { value: '', label: 'Select workflow' },
                                ...workflows.map((workflow) => ({
                                    value: workflow.id,
                                    label: workflow.title || workflow.name || workflow.id,
                                })),
                            ]}
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <div className={styles.textareaWrapper}>
                            <TextArea
                                id='json-config'
                                label='JSON Configuration:'
                                className={styles.jsonEditor}
                                value={configurationText}
                                onChange={(e) => setConfigurationText(e.target.value)}
                                placeholder='JSON configuration...'
                                disabled={loadingConfig || !selectedWorkflow}
                            />
                            <div className={styles.textareaButtons}>
                                <Button
                                    className={`${styles.textareaBtn} ${styles.clearBtn}`}
                                    onClick={handleClearJSON}
                                    disabled={loadingConfig || !configurationText}
                                    title='Clear JSON'
                                >
                                    Clear
                                </Button>
                                <Button
                                    className={`${styles.textareaBtn} ${styles.resetBtn}`}
                                    onClick={handleResetJSON}
                                    disabled={
                                        loadingConfig ||
                                        configurationText === originalConfigurationText ||
                                        !selectedWorkflow
                                    }
                                    title='Reset to original'
                                >
                                    Reset
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className={styles.buttonContainer}>
                        <div className={styles.leftButtons}>{/* Add any left-side buttons if needed */}</div>
                        <div className={styles.rightButtons}>
                            <Button
                                variant='primary'
                                onClick={handleSaveWorkflowDefinition}
                                disabled={loadingConfig || !selectedWorkflow}
                            >
                                Save Workflow
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Add Prompt Modal */}
            <AddPromptModal
                isOpen={isAddPromptModalOpen}
                onClose={() => setIsAddPromptModalOpen(false)}
                onSave={handleSavePromptFromModal}
                tenants={tenants}
            />

            {/* AlertModal for general alerts */}
            <AlertModal
                isOpen={isAlertOpen && !isDeleteConfirmOpen}
                onClose={closeAlert}
                message={alertMessage}
                type={alertType}
            />

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={isDeleteConfirmOpen}
                onClose={cancelDeletePrompt}
                title='Confirm Delete'
                size='sm'
                onCancel={cancelDeletePrompt}
                onDone={confirmDeletePrompt}
                cancelText='No, Cancel'
                doneText='Yes, Delete'
            >
                <p>{alertMessage}</p>
            </Modal>
        </div>
    );
};

export default SuperAdminDataEntry;
